import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { useCookieConsent } from 'contexts/CookieConsentContext';
import {
  BannerContainer, BannerContent, BannerTitle, BannerText, BannerLink, BannerActions,
  OutlineButton, PrimaryButton,
  ModalHeader, ModalTitle, CloseButton, ModalText, ModalLink, ModalFooter,
  AccordionSection, AccordionHeader, AccordionLeft, AccordionTitle, AlwaysActive, AccordionBody,
  modalStyles,
} from './styles';

// ─── Accordion Chevron ────────────────────────────────────────────────────────

const ChevronSvg = ({ open }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    style={{ transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)', flexShrink: 0 }}
  >
    <path d="M4 6l4 4 4-4" stroke="#718096" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ─── Toggle ───────────────────────────────────────────────────────────────────

const Toggle = ({ checked, onChange }) => (
  <div
    role="switch"
    aria-checked={checked}
    onClick={onChange}
    style={{
      width: '44px',
      height: '24px',
      backgroundColor: checked ? '#22c55e' : '#cbd5e0',
      borderRadius: '9999px',
      position: 'relative',
      cursor: 'pointer',
      flexShrink: 0,
      transition: 'background-color 0.2s',
    }}
  >
    <div
      style={{
        position: 'absolute',
        top: '3px',
        left: checked ? '23px' : '3px',
        width: '18px',
        height: '18px',
        backgroundColor: 'white',
        borderRadius: '50%',
        transition: 'left 0.2s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      }}
    />
  </div>
);

// ─── Accordion Sections Data ──────────────────────────────────────────────────

const SECTIONS = [
  {
    key: 'necessary',
    title: 'Cookies estritamente necessários',
    alwaysActive: true,
    description:
      'Estes cookies são necessários para que o website funcione e não podem ser desligados nos nossos sistemas. Normalmente, eles só são configurados em resposta a ações levadas a cabo por si e que correspondem a uma solicitação de serviços, tais como definir as suas preferências de privacidade, iniciar sessão ou preencher formulários. Pode configurar o seu navegador para bloquear ou alertá-lo(a) sobre esses cookies, mas algumas partes do website não funcionarão. Estes cookies não armazenam qualquer informação pessoal identificável.',
  },
  {
    key: 'advertising',
    title: 'Cookies de publicidade',
    description:
      'Estes cookies podem ser estabelecidos através do nosso site pelos nossos parceiros de publicidade. Podem ser usados por essas empresas para construir um perfil sobre os seus interesses e mostrar-lhe anúncios relevantes em outros websites. Eles não armazenam diretamente informações pessoais, mas são baseados na identificação exclusiva do seu navegador e dispositivo de internet. Se não permitir estes cookies, terá menos publicidade direcionada.',
  },
  {
    key: 'performance',
    title: 'Cookies de desempenho',
    description:
      'Estes cookies permitem-nos contar visitas e fontes de tráfego, para que possamos medir e melhorar o desempenho do nosso website. Eles ajudam-nos a saber quais são as páginas mais e menos populares e a ver como os visitantes se movimentam pelo website. Todas as informações recolhidas por estes cookies são agregadas e, por conseguinte, anónimas.',
  },
  {
    key: 'functionality',
    title: 'Cookies de funcionalidade',
    description:
      'Estes cookies permitem que o site forneça uma funcionalidade e personalização melhoradas. Podem ser estabelecidos por nós ou por fornecedores externos cujos serviços adicionámos às nossas páginas. Se não permitir estes cookies algumas destas funcionalidades, ou mesmo todas, podem não atuar corretamente.',
  },
  {
    key: 'thirdParty',
    title: 'Cookies de terceiros',
    description:
      'Alguns recursos do nosso site utilizam serviços fornecidos por terceiros, como ferramentas de análise, mapas, vídeos incorporados e botões de compartilhamento em redes sociais. Esses parceiros podem definir seus próprios cookies para identificar o seu navegador quando você interage com esses serviços, mesmo fora do nosso site. O uso desses cookies está sujeito à política de privacidade de cada fornecedor.',
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

export default function CookiePolicy() {
  const { showBanner, showModal, openSettings, closeSettings, acceptAll, rejectAll, savePreferences } =
    useCookieConsent();

  useEffect(() => {
    document.body.style.overflow = showModal ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [showModal]);

  const [expanded, setExpanded] = useState({ necessary: true });
  const [preferences, setPreferences] = useState({
    advertising: true,
    performance: true,
    functionality: true,
    thirdParty: true,
  });

  const toggleSection = (key) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const togglePreference = (key) => {
    setPreferences((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <>
      {showBanner && (
        <BannerContainer>
          <BannerContent>
            <BannerTitle>Levamos a privacidade muito a sério</BannerTitle>
            <BannerText>
              Usamos cookies para personalizar conteúdo e anúncios, analisar tráfego e aprimorar a sua experiência de
              navegação. Ao clicar em "Aceitar tudo", você concorda com o uso de cookies. Leia nossa{' '}
              <BannerLink href="/cookies-policy">Política de Cookies</BannerLink>
            </BannerText>
            <BannerActions>
              <OutlineButton onClick={openSettings}>Personalizar</OutlineButton>
              <PrimaryButton onClick={acceptAll}>Aceitar tudo</PrimaryButton>
            </BannerActions>
          </BannerContent>
        </BannerContainer>
      )}

      <Modal
        isOpen={showModal}
        onRequestClose={closeSettings}
        style={modalStyles}
        contentLabel="Configurações de cookies"
      >
        <div style={{ overflowY: 'auto', flex: 1, padding: '32px 32px 0 32px' }}>
          <ModalHeader>
            <ModalTitle>Configurações de cookies</ModalTitle>
            <CloseButton onClick={closeSettings} aria-label="Fechar">
              ×
            </CloseButton>
          </ModalHeader>

          <ModalText>
            Usamos cookies para personalizar conteúdo e anúncios, analisar tráfego e aprimorar a sua experiência de
            navegação. Ao clicar em "Aceitar tudo", você concorda com o uso de cookies.
          </ModalText>
          <ModalLink href="/cookies-policy">Leia nossa Política de Cookies</ModalLink>

          <div style={{ marginTop: '24px' }}>
            {SECTIONS.map((section) => (
              <AccordionSection key={section.key}>
                <AccordionHeader onClick={() => toggleSection(section.key)}>
                  <AccordionLeft>
                    <ChevronSvg open={expanded[section.key]} />
                    <AccordionTitle>{section.title}</AccordionTitle>
                  </AccordionLeft>
                  {section.alwaysActive ? (
                    <AlwaysActive>Sempre ativos</AlwaysActive>
                  ) : (
                    <div onClick={(e) => e.stopPropagation()}>
                      <Toggle
                        checked={preferences[section.key]}
                        onChange={() => togglePreference(section.key)}
                      />
                    </div>
                  )}
                </AccordionHeader>
                {expanded[section.key] && <AccordionBody>{section.description}</AccordionBody>}
              </AccordionSection>
            ))}
          </div>
        </div>

        <div style={{ padding: '0 32px 32px 32px', flexShrink: 0 }}>
          <ModalFooter>
            <OutlineButton onClick={() => { rejectAll(); }}>Rejeitar tudo</OutlineButton>
            <PrimaryButton onClick={() => savePreferences(preferences)}>Salvar</PrimaryButton>
          </ModalFooter>
        </div>
      </Modal>
    </>
  );
}
